/**
 * Customer Portal (UI) - messages route with modal detail view.
 *
 * Behavior intentionally mirrors customer-portal-early-test:
 * - list messages from /api/sms/messages for authenticated session account (backend)
 * - open modal on row click
 * - fetch detail from /api/sms/messages/:id
 */
import { type MouseEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatPortalDateParts, formatPortalDateTime, type TimeDisplayMode } from '../utils/datetime';
import { fetchWebhookJobDetails } from '../features/api/client';
import type {
  MessageLifecycleStep,
  MessagingCapabilities,
  PortalMessageLifecycle,
  WebhookJobDetailApiResponse
} from '../types/portal';

type SmsMessage = {
  prontoMessageId: string;
  reference: string | null;
  type: 'SMS' | 'MMS' | 'RCS';
  templateId: string | null;
  fromNumber: string;
  toNumber: string;
  status: string;
  errorCategory: string | null;
  errorReason: string | null;
  createdAt: string;
  segments: number | null;
  carrier: string | null;
  renderedBody:
    | string
    | Array<{
        type: 'text' | 'media_url' | 'binary_media' | 'media_r2';
        value: string;
        contentType?: string | null;
        r2Key?: string;
      }>
    | null;
  mediaPayload: Array<{
    mediaId?: string;
    index?: number;
    sourceType?: 'url' | 'inline_base64' | 'inline_plain';
    url?: string;
    r2Key?: string;
    contentType?: string | null;
    sizeBytes?: number | null;
    ingestStatus?: 'PENDING' | 'READY' | 'FAILED';
    ingestError?: string | null;
  }> | null;
  direction: 'outbound' | 'inbound';
};

type SmsMessageDetail = {
  direction: 'outbound' | 'inbound';
  message: {
    prontoMessageId: string;
    reference: string | null;
    type: 'SMS' | 'MMS' | 'RCS';
    templateId: string | null;
    fromNumber: string;
    toNumber: string;
    status: string;
    errorCategory: string | null;
    errorReason: string | null;
    createdAt: string;
    segments: number | null;
    carrier: string | null;
    renderedBody:
      | string
      | Array<{
          type: 'text' | 'media_url' | 'binary_media' | 'media_r2';
          value: string;
          contentType?: string | null;
          r2Key?: string;
        }>
      | null;
    mediaPayload: Array<{
      mediaId?: string;
      index?: number;
      sourceType?: 'url' | 'inline_base64' | 'inline_plain';
      url?: string;
      r2Key?: string;
      contentType?: string | null;
      sizeBytes?: number | null;
      ingestStatus?: 'PENDING' | 'READY' | 'FAILED';
      ingestError?: string | null;
    }> | null;
  } | null;
  lifecycle: PortalMessageLifecycle;
};

type DirectionFilter = 'all' | 'outbound' | 'inbound';
type SortBy =
  | 'createdAt'
  | 'status'
  | 'type'
  | 'direction'
  | 'reference'
  | 'templateId'
  | 'fromNumber'
  | 'toNumber';
type SortDir = 'asc' | 'desc';
type MessageTab = 'sms' | 'mms' | 'rcs' | 'whatsapp';
type LifecyclePayloadModalState = {
  title: string;
  subtitle: string;
  payloadText: string;
  webhookRef?: MessageLifecycleStep['webhookRef'];
};
type WebhookAttemptsModalState = {
  title: string;
  subtitle: string;
  loading: boolean;
  details: WebhookJobDetailApiResponse | null;
  error: string | null;
};
type MessagePreviewPart =
  | { id: string; kind: 'text'; text: string }
  | { id: string; kind: 'media'; url: string | null; contentType?: string | null; label: string };

type SmsQueryState = {
  direction: DirectionFilter;
  status: string;
  templateId: string;
  fromNumber: string;
  toNumber: string;
  sortBy: SortBy;
  sortDir: SortDir;
};

type SearchMode = 'prontoMessageId' | 'reference';
type SearchChannelScope = 'all' | 'SMS' | 'MMS' | 'RCS';

type SortableMessageColumn = {
  key: SortBy;
  label: string;
};

const STATUS_OPTIONS = [
  '',
  'PENDING_ASSET',
  'QUEUED',
  'SENDING',
  'HELD',
  'REJECTED',
  'RECEIVED',
  'SENT',
  'DELIVERED',
  'FAILED',
  'EXPIRED',
  'CANCELLED'
] as const;

const SORTABLE_MESSAGE_COLUMNS: SortableMessageColumn[] = [
  { key: 'createdAt', label: 'Created' },
  { key: 'reference', label: 'Reference' },
  { key: 'templateId', label: 'Template ID' },
  { key: 'type', label: 'Channel' },
  { key: 'direction', label: 'Direction' },
  { key: 'fromNumber', label: 'From' },
  { key: 'toNumber', label: 'To' },
  { key: 'status', label: 'Status' }
];

function defaultSortDirForColumn(sortBy: SortBy): SortDir {
  return sortBy === 'createdAt' ? 'desc' : 'asc';
}

function nextSortState(current: Pick<SmsQueryState, 'sortBy' | 'sortDir'>, targetSortBy: SortBy): Pick<SmsQueryState, 'sortBy' | 'sortDir'> {
  if (current.sortBy === targetSortBy) {
    return {
      sortBy: targetSortBy,
      sortDir: current.sortDir === 'asc' ? 'desc' : 'asc'
    };
  }
  return {
    sortBy: targetSortBy,
    sortDir: defaultSortDirForColumn(targetSortBy)
  };
}

function SortIndicatorIcon({ active, sortDir }: { active: boolean; sortDir: SortDir }) {
  if (active && sortDir === 'asc') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-sm text-primary" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M6 15l6 -6l6 6" />
      </svg>
    );
  }
  if (active) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-sm text-primary" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M6 9l6 6l6 -6" />
      </svg>
    );
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-sm text-secondary opacity-75" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M8 7l4 -4l4 4" />
      <path d="M8 17l4 4l4 -4" />
      <path d="M12 3v18" />
    </svg>
  );
}

const OUTBOUND_LIFECYCLE_STEPS = [
  { id: 'Submitted', label: 'Customer Request', eventTypes: ['Submitted'] },
  { id: 'SentUpstream', label: 'Sent Upstream', eventTypes: ['SentUpstream'] },
  { id: 'SentToMNO', label: 'Sent To MNO', eventTypes: ['SentToMNO'] },
  { id: 'DeliveryStatus', label: 'Delivery Status', eventTypes: ['DeliveryStatus'] },
  { id: 'DLRReceived', label: 'DLR Received', eventTypes: ['DLRReceived'] },
  { id: 'DLRSent', label: 'DLR Webhook Sent', eventTypes: ['DLRSent'] }
] as const;

const INBOUND_LIFECYCLE_STEPS = [
  { id: 'Received', label: 'Message Received', eventTypes: ['Received'] },
  { id: 'Sent', label: 'Message Webhook Sent', eventTypes: ['Sent'] }
] as const;

function renderBodyForCell(
  renderedBody:
    | string
    | Array<{
        type: 'text' | 'media_url' | 'binary_media' | 'media_r2';
        value: string;
      }>
    | null
): string {
  if (typeof renderedBody === 'string') {
    return renderedBody || '-';
  }
  if (!Array.isArray(renderedBody) || renderedBody.length === 0) {
    return '-';
  }
  let textPartCount = 0;
  let mediaPartCount = 0;
  return renderedBody
    .map((item) => {
      if (item.type === 'text') {
        textPartCount += 1;
        return `Text Part ${textPartCount}: ${item.value}`;
      }
      mediaPartCount += 1;
      return `Media Part ${mediaPartCount}: ${item.type}`;
    })
    .join(' | ');
}

function normalizeSmsTextBody(
  renderedBody:
    | string
    | Array<{
        type: 'text' | 'media_url' | 'binary_media' | 'media_r2';
        value: string;
      }>
    | null
): string {
  if (typeof renderedBody === 'string') {
    return renderedBody.trim();
  }
  if (!Array.isArray(renderedBody)) {
    return '';
  }
  return renderedBody
    .filter((part) => part.type === 'text' && part.value.trim().length > 0)
    .map((part) => part.value.trim())
    .join('\n');
}

function toPortalMediaViewUrl(input: {
  prontoMessageId: string;
  direction: 'inbound' | 'outbound';
  type: 'SMS' | 'MMS' | 'RCS';
  index: number;
}): string | null {
  if (!input.prontoMessageId || !Number.isInteger(input.index) || input.index < 0) {
    return null;
  }
  const params = new URLSearchParams({
    prontoMessageId: input.prontoMessageId,
    direction: input.direction,
    type: input.type,
    index: String(input.index)
  });
  return `/api/sms/media/view?${params.toString()}`;
}

function toMessagePreviewParts(
  message: SmsMessageDetail['message'],
  direction: 'outbound' | 'inbound'
): MessagePreviewPart[] {
  if (!message) {
    return [];
  }

  if (message.type === 'SMS') {
    const smsBody = normalizeSmsTextBody(message.renderedBody);
    return smsBody.length > 0 ? [{ id: 'sms-text', kind: 'text', text: smsBody }] : [];
  }

  if (message.type === 'RCS') {
    const rcsBody = normalizeSmsTextBody(message.renderedBody);
    return rcsBody.length > 0 ? [{ id: 'rcs-text', kind: 'text', text: rcsBody }] : [];
  }

  if (direction === 'inbound') {
    const parts: MessagePreviewPart[] = [];
    const inboundText = normalizeSmsTextBody(message.renderedBody);
    if (inboundText.length > 0) {
      parts.push({ id: 'mms-inbound-text', kind: 'text', text: inboundText });
    }
    (message.mediaPayload ?? []).forEach((item, index) => {
      const mediaIndex = Number.isInteger(item.index) && Number(item.index) >= 0 ? Number(item.index) : index;
      parts.push({
        id: `mms-inbound-media-${index}`,
        kind: 'media',
        url: toPortalMediaViewUrl({
          prontoMessageId: message.prontoMessageId,
          direction,
          type: message.type,
          index: mediaIndex
        }),
        contentType: item.contentType ?? null,
        label:
          item.ingestStatus === 'FAILED'
            ? `Inbound media part ${index + 1} (ingest failed)`
            : `Inbound media part ${index + 1}`
      });
    });
    return parts;
  }

  if (!Array.isArray(message.renderedBody)) {
    const outboundFallback = normalizeSmsTextBody(message.renderedBody);
    return outboundFallback.length > 0 ? [{ id: 'mms-outbound-fallback', kind: 'text', text: outboundFallback }] : [];
  }

  return message.renderedBody.map((part, index) => {
    if (part.type === 'text') {
      return {
        id: `mms-outbound-text-${index}`,
        kind: 'text',
        text: part.value
      };
    }
    if (part.type === 'media_url') {
      return {
        id: `mms-outbound-media-url-${index}`,
        kind: 'media',
        url: part.value.trim().length > 0 ? part.value.trim() : null,
        contentType: part.contentType ?? null,
        label: `Outbound media URL part ${index + 1}`
      };
    }
    if (part.type === 'media_r2') {
      return {
        id: `mms-outbound-media-r2-${index}`,
        kind: 'media',
        url: null,
        label: `Outbound media part ${index + 1} (R2 asset)`
      };
    }
    return {
      id: `mms-outbound-media-binary-${index}`,
      kind: 'media',
      url: null,
      label: `Outbound media part ${index + 1} (binary media)`
    };
  });
}

function MessagePreviewMediaPart({ part }: { part: Extract<MessagePreviewPart, { kind: 'media' }> }) {
  const [previewUnavailable, setPreviewUnavailable] = useState<boolean>(false);
  const canRenderImage = Boolean(part.url) && !previewUnavailable;
  if (!canRenderImage) {
    if (part.url) {
      return (
        <a
          className="message-body-media-placeholder text-decoration-none"
          href={part.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${part.label} in a new tab`}
        >
          <div className="fw-semibold">Media preview unavailable</div>
          <div className="small text-secondary">{part.label}</div>
        </a>
      );
    }
    return (
      <div className="message-body-media-placeholder">
        <div className="fw-semibold">Media preview unavailable</div>
        <div className="small text-secondary">{part.label}</div>
      </div>
    );
  }
  return (
    <a
      className="message-body-media-tile"
      href={part.url ?? '#'}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${part.label} in a new tab`}
      onClick={(event) => {
        if (!part.url) {
          event.preventDefault();
        }
      }}
    >
      <img
        src={part.url ?? undefined}
        alt={part.label}
        className="message-body-media-thumb"
        loading="lazy"
        onError={() => setPreviewUnavailable(true)}
      />
    </a>
  );
}

function MessagePreviewPartRenderer({ part }: { part: MessagePreviewPart }) {
  if (part.kind === 'text') {
    return (
      <div className="message-body-text-part portal-wrap">
        {part.text}
      </div>
    );
  }
  return <MessagePreviewMediaPart part={part} />;
}

function isLifecycleSuccess(stepStatus: MessageLifecycleStep['status'] | null): boolean {
  return stepStatus === 'SUCCESS' || stepStatus === 'SKIPPED';
}

function getLifecycleStepState(
  lifecycle: SmsMessageDetail['lifecycle'] | null | undefined,
  stepId: string
): MessageLifecycleStep | null {
  if (!lifecycle || typeof lifecycle !== 'object') {
    return null;
  }
  const record = lifecycle as Record<string, unknown>;
  const candidate = record[stepId];
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return null;
  }
  return candidate as MessageLifecycleStep;
}

/**
 * Pretty-print any lifecycle payload shape for tooltip panels (never throws).
 */
function formatLifecyclePayloadForDisplay(payload: unknown): string {
  if (payload == null) {
    return '(no payload)';
  }
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (trimmed.length === 0) {
      return '(empty payload)';
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return trimmed;
    }
  }
  if (typeof payload === 'object') {
    try {
      return JSON.stringify(payload, null, 2);
    } catch {
      return String(payload);
    }
  }
  return String(payload);
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Request failed (${response.status}) for ${url}`);
  }
  const payload = (await response.json()) as unknown;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return payload as T;
  }
  const record = payload as Record<string, unknown>;
  if (
    typeof record.SchemaVersion === 'string' &&
    typeof record.RequestedAt === 'string' &&
    Object.prototype.hasOwnProperty.call(record, 'Data')
  ) {
    return record.Data as T;
  }
  return payload as T;
}

type MessagesRouteProps = {
  accountId: string | null;
  timeDisplayMode: TimeDisplayMode;
  messagingCapabilities: MessagingCapabilities | null;
};

export function MessagesRoute({ accountId, timeDisplayMode, messagingCapabilities }: MessagesRouteProps) {
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<MessageTab>('sms');
  const [draftQuery, setDraftQuery] = useState<SmsQueryState>({
    direction: 'outbound',
    status: '',
    templateId: '',
    fromNumber: '',
    toNumber: '',
    sortBy: 'createdAt',
    sortDir: 'desc'
  });
  const [appliedQuery, setAppliedQuery] = useState<SmsQueryState>({
    direction: 'outbound',
    status: '',
    templateId: '',
    fromNumber: '',
    toNumber: '',
    sortBy: 'createdAt',
    sortDir: 'desc'
  });
  const [selectedMessage, setSelectedMessage] = useState<SmsMessage | null>(null);
  const [smsDetail, setSmsDetail] = useState<SmsMessageDetail | null>(null);
  const [smsDetailLoading, setSmsDetailLoading] = useState(false);
  const [lifecyclePayloadModal, setLifecyclePayloadModal] = useState<LifecyclePayloadModalState | null>(null);
  const [webhookAttemptsModal, setWebhookAttemptsModal] = useState<WebhookAttemptsModalState | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [searchModalOpen, setSearchModalOpen] = useState<boolean>(false);
  const [searchMode, setSearchMode] = useState<SearchMode>('prontoMessageId');
  const [searchProntoMessageId, setSearchProntoMessageId] = useState<string>('');
  const [searchReference, setSearchReference] = useState<string>('');
  const [searchChannelScope, setSearchChannelScope] = useState<SearchChannelScope>('all');
  const [searchDirectionScope, setSearchDirectionScope] = useState<DirectionFilter>('all');
  const [searchSubmitting, setSearchSubmitting] = useState<boolean>(false);
  const resolvedMessagingCapabilities = messagingCapabilities ?? {
    sms: false,
    mms: false,
    rcs: false,
    whatsapp: false
  };
  const visibleTabs = useMemo<MessageTab[]>(() => {
    const tabs: MessageTab[] = [];
    if (resolvedMessagingCapabilities.sms) {
      tabs.push('sms');
    }
    if (resolvedMessagingCapabilities.mms) {
      tabs.push('mms');
    }
    if (resolvedMessagingCapabilities.rcs) {
      tabs.push('rcs');
    }
    if (resolvedMessagingCapabilities.whatsapp) {
      tabs.push('whatsapp');
    }
    return tabs;
  }, [resolvedMessagingCapabilities.mms, resolvedMessagingCapabilities.rcs, resolvedMessagingCapabilities.sms, resolvedMessagingCapabilities.whatsapp]);
  const activeMessageType: 'SMS' | 'MMS' | 'RCS' | null =
    activeTab === 'sms' ? 'SMS' : activeTab === 'mms' ? 'MMS' : activeTab === 'rcs' ? 'RCS' : null;

  useEffect(() => {
    setLifecyclePayloadModal(null);
    setWebhookAttemptsModal(null);
  }, [smsDetail?.message?.prontoMessageId]);

  useEffect(() => {
    if (visibleTabs.length === 0) {
      return;
    }
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [activeTab, visibleTabs]);

  useEffect(() => {
    if (!lifecyclePayloadModal && !webhookAttemptsModal && !searchModalOpen) {
      return;
    }
    const onWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setLifecyclePayloadModal(null);
        setWebhookAttemptsModal(null);
        setSearchModalOpen(false);
      }
    };
    window.addEventListener('keydown', onWindowKeyDown);
    return () => window.removeEventListener('keydown', onWindowKeyDown);
  }, [lifecyclePayloadModal, webhookAttemptsModal, searchModalOpen]);

  useEffect(() => {
    if (!activeMessageType) {
      setLoading(false);
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const query = new URLSearchParams({
      limit: '100',
      sortBy: appliedQuery.sortBy,
      sortDir: appliedQuery.sortDir
    });
    if (accountId && accountId.trim().length > 0) {
      query.set('accountId', accountId.trim());
    }
    if (appliedQuery.status) {
      query.set('status', appliedQuery.status);
    }
    if (appliedQuery.templateId.trim().length > 0) {
      query.set('templateId', appliedQuery.templateId.trim());
    }
    if (appliedQuery.fromNumber.trim().length > 0) {
      query.set('from', appliedQuery.fromNumber.trim());
    }
    if (appliedQuery.toNumber.trim().length > 0) {
      query.set('to', appliedQuery.toNumber.trim());
    }
    query.set('type', activeMessageType);
    if (appliedQuery.direction !== 'all') {
      query.set('direction', appliedQuery.direction);
    }

    getJson<unknown>(`/api/sms/messages?${query.toString()}`)
      .then((messagesResult) => {
        if (cancelled) return;
        if (!messagesResult || typeof messagesResult !== 'object' || Array.isArray(messagesResult)) {
          throw new Error('Contract violation: Messages response must be an object.');
        }
        const items = (messagesResult as Record<string, unknown>).Items;
        if (!Array.isArray(items)) {
          throw new Error('Contract violation: Messages response must include Data.Items array.');
        }
        setMessages(items as SmsMessage[]);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, appliedQuery, activeMessageType]);

  const displayedMessages = useMemo(() => {
    const filtered = messages.filter((message) => {
      if (appliedQuery.direction !== 'all' && message.direction !== appliedQuery.direction) {
        return false;
      }
      return true;
    });
    const sorted = [...filtered].sort((left, right) => {
      const compareString = (a: string | null | undefined, b: string | null | undefined): number =>
        String(a ?? '').localeCompare(String(b ?? ''), undefined, { sensitivity: 'base', numeric: true });
      const compareDate = (a: string, b: string): number => {
        const aTs = Date.parse(a);
        const bTs = Date.parse(b);
        if (Number.isFinite(aTs) && Number.isFinite(bTs) && aTs !== bTs) {
          return aTs < bTs ? -1 : 1;
        }
        return compareString(a, b);
      };
      let base = 0;
      if (appliedQuery.sortBy === 'createdAt') base = compareDate(left.createdAt, right.createdAt);
      if (appliedQuery.sortBy === 'status') base = compareString(left.status, right.status);
      if (appliedQuery.sortBy === 'type') base = compareString(left.type, right.type);
      if (appliedQuery.sortBy === 'direction') base = compareString(left.direction, right.direction);
      if (appliedQuery.sortBy === 'reference') base = compareString(left.reference, right.reference);
      if (appliedQuery.sortBy === 'templateId') base = compareString(left.templateId, right.templateId);
      if (appliedQuery.sortBy === 'fromNumber') base = compareString(left.fromNumber, right.fromNumber);
      if (appliedQuery.sortBy === 'toNumber') base = compareString(left.toNumber, right.toNumber);
      if (base === 0) {
        base = compareDate(left.createdAt, right.createdAt);
      }
      return appliedQuery.sortDir === 'asc' ? base : base * -1;
    });
    return sorted;
  }, [appliedQuery.direction, appliedQuery.sortBy, appliedQuery.sortDir, messages]);

  const hasAppliedFilters =
    appliedQuery.direction !== 'outbound' ||
    appliedQuery.status.length > 0 ||
    appliedQuery.templateId.trim().length > 0 ||
    appliedQuery.fromNumber.trim().length > 0 ||
    appliedQuery.toNumber.trim().length > 0;

  function onColumnSortClick(column: SortBy, event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setDraftQuery((prev) => ({ ...prev, ...nextSortState(prev, column) }));
    setAppliedQuery((prev) => ({ ...prev, ...nextSortState(prev, column) }));
  }

  async function showSmsDetail(message: SmsMessage) {
    setSelectedMessage(message);
    setSmsDetailLoading(true);
    setSmsDetail(null);
    try {
      const res = await getJson<{
        Message: SmsMessageDetail['message'];
        Lifecycle: SmsMessageDetail['lifecycle'];
        Direction: SmsMessageDetail['direction'];
      }>(
        `/api/sms/messages/${encodeURIComponent(message.prontoMessageId)}`
      );
      if (!res.Message) {
        throw new Error('No message detail was found for that message ID.');
      }
      setSelectedMessage({
        ...res.Message,
        direction: res.Direction
      });
      setSmsDetail({
        message: res.Message,
        lifecycle: res.Lifecycle,
        direction: res.Direction
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSmsDetailLoading(false);
    }
  }

  async function lookupMessageById(rawMessageId: string): Promise<boolean> {
    const normalized = rawMessageId.trim();
    if (!normalized) {
      setError('Enter a Message ID to look up.');
      return false;
    }
    setError(null);
    setSmsDetailLoading(true);
    setSmsDetail(null);
    try {
      const res = await getJson<{
        Message: SmsMessageDetail['message'];
        Lifecycle: SmsMessageDetail['lifecycle'];
        Direction: SmsMessageDetail['direction'];
      }>(`/api/sms/messages/${encodeURIComponent(normalized)}`);
      if (!res.Message) {
        throw new Error('No message detail was found for that message ID.');
      }
      setSelectedMessage({
        ...res.Message,
        direction: res.Direction
      });
      setSmsDetail({
        message: res.Message,
        lifecycle: res.Lifecycle,
        direction: res.Direction
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setSmsDetailLoading(false);
    }
  }

  async function submitMessageSearch(): Promise<void> {
    if (searchSubmitting) {
      return;
    }
    setSearchSubmitting(true);
    setError(null);
    try {
      if (searchMode === 'prontoMessageId') {
        const found = await lookupMessageById(searchProntoMessageId);
        if (found) {
          setSearchModalOpen(false);
        }
        return;
      }

      const reference = searchReference.trim();
      if (reference.length === 0) {
        setError('Enter a reference to search.');
        return;
      }

      const query = new URLSearchParams({
        limit: '10',
        reference
      });
      if (accountId && accountId.trim().length > 0) {
        query.set('accountId', accountId.trim());
      }
      if (searchChannelScope !== 'all') {
        query.set('type', searchChannelScope);
      }
      if (searchDirectionScope !== 'all') {
        query.set('direction', searchDirectionScope);
      }

      const response = await getJson<unknown>(`/api/sms/messages?${query.toString()}`);
      if (!response || typeof response !== 'object' || Array.isArray(response)) {
        throw new Error('Contract violation: Messages response must be an object.');
      }
      const items = (response as Record<string, unknown>).Items;
      if (!Array.isArray(items) || items.length === 0) {
        setError('No messages matched that reference and scope.');
        return;
      }
      const first = items[0] as SmsMessage;
      const found = await lookupMessageById(first.prontoMessageId);
      if (found) {
        setSearchModalOpen(false);
      }
    } finally {
      setSearchSubmitting(false);
    }
  }

  async function openWebhookAttemptModal(
    webhookRef: NonNullable<MessageLifecycleStep['webhookRef']>,
    direction: 'outbound' | 'inbound'
  ): Promise<void> {
    const title = direction === 'inbound' ? 'Message webhook attempts' : 'Customer webhook attempts';
    const subtitle = direction === 'inbound' ? 'Inbound message webhook deliveries' : 'Delivery-report webhook deliveries';
    setWebhookAttemptsModal({
      title,
      subtitle,
      loading: true,
      details: null,
      error: null
    });

    try {
      if (!Number.isFinite(webhookRef.jobId) || webhookRef.jobId < 1 || !webhookRef.shardName.trim()) {
        setWebhookAttemptsModal({
          title,
          subtitle,
          loading: false,
          details: null,
          error: 'No direct webhook reference available for this lifecycle step.'
        });
        return;
      }
      const details = await fetchWebhookJobDetails(Math.floor(webhookRef.jobId), webhookRef.shardName);
      if (!details.job) {
        setWebhookAttemptsModal({
          title,
          subtitle,
          loading: false,
          details: null,
          error: 'No webhook attempts were found for this lifecycle step.'
        });
        return;
      }
      setWebhookAttemptsModal({
        title,
        subtitle,
        loading: false,
        details,
        error: null
      });
    } catch (error) {
      setWebhookAttemptsModal({
        title,
        subtitle,
        loading: false,
        details: null,
        error: `Unable to load webhook attempts: ${error instanceof Error ? error.message : String(error)}`
      });
    }
  }

  function openLifecyclePayloadModal(step: MessageLifecycleStep, direction: 'outbound' | 'inbound') {
    const title = direction === 'inbound' ? 'Message lifecycle payload' : 'Customer lifecycle payload';
    const subtitle =
      step.webhookRef?.webhookType === 'incoming_message'
        ? 'Inbound webhook payload'
        : step.webhookRef?.webhookType === 'delivery_report'
          ? 'Delivery-report webhook payload'
          : step.webhookRef?.webhookType === 'rcs_read_receipt'
            ? 'RCS read-receipt webhook payload'
            : step.webhookRef?.webhookType === 'rcs_user_action'
              ? 'RCS user-action webhook payload'
          : 'Customer request payload';
    setLifecyclePayloadModal({
      title,
      subtitle,
      payloadText: formatLifecyclePayloadForDisplay(step.payload ?? null),
      webhookRef: step.webhookRef
    });
  }

  async function triggerExportCurrentView(): Promise<void> {
    const query = new URLSearchParams({
      format: 'csv',
      pageSize: '200'
    });
    if (accountId && accountId.trim().length > 0) {
      query.set('accountId', accountId.trim());
    }
    if (appliedQuery.status) {
      query.set('status', appliedQuery.status);
    }
    if (appliedQuery.templateId.trim().length > 0) {
      query.set('templateId', appliedQuery.templateId.trim());
    }
    if (appliedQuery.fromNumber.trim().length > 0) {
      query.set('from', appliedQuery.fromNumber.trim());
    }
    if (appliedQuery.toNumber.trim().length > 0) {
      query.set('to', appliedQuery.toNumber.trim());
    }
    if (activeMessageType) {
      query.set('type', activeMessageType);
    }
    if (appliedQuery.direction !== 'all') {
      query.set('direction', appliedQuery.direction);
    }

    setExporting(true);
    setError(null);
    setExportNotice(null);
    try {
      const response = await fetch(`/api/sms/messages?${query.toString()}`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`Export trigger failed (${response.status})`);
      }
      setExportNotice('Export started. Track status and download in Reports > Export Jobs.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }

  const selectedDirection = smsDetail?.direction ?? selectedMessage?.direction ?? 'outbound';
  const lifecycleSteps =
    selectedDirection === 'inbound' ? INBOUND_LIFECYCLE_STEPS : OUTBOUND_LIFECYCLE_STEPS;
  const lifecycleDetail = smsDetail?.lifecycle ?? null;
  const messagePreviewParts = useMemo(
    () => toMessagePreviewParts(smsDetail?.message ?? null, selectedDirection),
    [selectedDirection, smsDetail?.message]
  );

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between gap-3 align-items-end mb-3">
        <div>
          <div className="page-pretitle">Messaging</div>
          <h2 className="page-title mb-0">Messages</h2>
        </div>
        <div className="d-flex flex-wrap align-items-end gap-2 ms-auto">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => {
              setSearchModalOpen(true);
            }}
          >
            Search by ID
          </button>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => {
              void triggerExportCurrentView();
            }}
            disabled={exporting}
          >
            {exporting ? 'Starting export...' : 'Export current view (CSV)'}
          </button>
          <Link to="/reports" className="btn btn-ghost-secondary btn-sm">
            View Export Jobs
          </Link>
        </div>
      </div>
      {loading ? <div className="alert alert-info">Loading message activity...</div> : null}
      {error ? <div className="alert alert-danger">{error}</div> : null}
      <div className="card">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs mb-0" role="tablist">
            {visibleTabs.includes('sms') ? (
              <li className="nav-item" role="presentation">
                <button
                  type="button"
                  className={`nav-link${activeTab === 'sms' ? ' active' : ''}`}
                  onClick={() => setActiveTab('sms')}
                >
                  SMS
                </button>
              </li>
            ) : null}
            {visibleTabs.includes('mms') ? (
              <li className="nav-item" role="presentation">
                <button
                  type="button"
                  className={`nav-link${activeTab === 'mms' ? ' active' : ''}`}
                  onClick={() => setActiveTab('mms')}
                >
                  MMS
                </button>
              </li>
            ) : null}
            {visibleTabs.includes('rcs') ? (
              <li className="nav-item" role="presentation">
                <button
                  type="button"
                  className={`nav-link${activeTab === 'rcs' ? ' active' : ''}`}
                  onClick={() => setActiveTab('rcs')}
                >
                  RCS
                </button>
              </li>
            ) : null}
            {visibleTabs.includes('whatsapp') ? (
              <li className="nav-item" role="presentation">
                <button
                  type="button"
                  className={`nav-link${activeTab === 'whatsapp' ? ' active' : ''}`}
                  onClick={() => setActiveTab('whatsapp')}
                >
                  WhatsApp
                </button>
              </li>
            ) : null}
          </ul>
        </div>
        {visibleTabs.length === 0 ? (
          <div className="card-body">
            <div className="alert alert-warning mb-0">No messaging channels are enabled for this account.</div>
          </div>
        ) : null}
        {(activeTab === 'sms' || activeTab === 'mms' || activeTab === 'rcs') && activeMessageType ? (
          <>
            <div className="card-body border-bottom">
              <div className="row g-2 align-items-end">
                <div className="col-sm-6 col-lg-2">
                  <label className="form-label mb-1">Direction</label>
                  <select
                    className="form-select form-select-sm"
                    value={draftQuery.direction}
                    onChange={(event) =>
                      setDraftQuery((prev) => ({ ...prev, direction: event.target.value as DirectionFilter }))
                    }
                  >
                    <option value="all">All</option>
                    <option value="outbound">Outbound</option>
                    <option value="inbound">Inbound</option>
                  </select>
                </div>
                <div className="col-sm-6 col-lg-2">
                  <label className="form-label mb-1">Status</label>
                  <select
                    className="form-select form-select-sm"
                    value={draftQuery.status}
                    onChange={(event) => setDraftQuery((prev) => ({ ...prev, status: event.target.value }))}
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status || 'all'} value={status}>
                        {status || 'All'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-6 col-lg-2">
                  <label className="form-label mb-1">Template ID</label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    placeholder="template-key"
                    value={draftQuery.templateId}
                    onChange={(event) => setDraftQuery((prev) => ({ ...prev, templateId: event.target.value }))}
                  />
                </div>
                <div className="col-sm-6 col-lg-2">
                  <label className="form-label mb-1">From number</label>
                  <input
                    className="form-control form-control-sm"
                    type="tel"
                    placeholder="+15551234567"
                    value={draftQuery.fromNumber}
                    onChange={(event) => setDraftQuery((prev) => ({ ...prev, fromNumber: event.target.value }))}
                  />
                </div>
                <div className="col-sm-6 col-lg-2">
                  <label className="form-label mb-1">To number</label>
                  <input
                    className="form-control form-control-sm"
                    type="tel"
                    placeholder="+15551234567"
                    value={draftQuery.toNumber}
                    onChange={(event) => setDraftQuery((prev) => ({ ...prev, toNumber: event.target.value }))}
                  />
                </div>
                <div className="col-12 col-lg-2">
                  <div className="d-flex gap-2 justify-content-lg-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => setAppliedQuery(draftQuery)}
                      disabled={loading}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost-secondary btn-sm"
                      onClick={() => {
                        const reset: SmsQueryState = {
                          direction: 'outbound',
                          status: '',
                          templateId: '',
                          fromNumber: '',
                          toNumber: '',
                          sortBy: 'createdAt',
                          sortDir: 'desc'
                        };
                        setDraftQuery(reset);
                        setAppliedQuery(reset);
                      }}
                      disabled={loading}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {exportNotice ? (
              <div className="card-body border-bottom">
                <div className="alert alert-success py-2 mb-0">{exportNotice}</div>
              </div>
            ) : null}
            <div className="table-responsive">
              <table className="table card-table table-vcenter">
                <thead>
                  <tr>
                    {SORTABLE_MESSAGE_COLUMNS.map((column) => {
                      const isActive = appliedQuery.sortBy === column.key;
                      const ariaSort = isActive ? (appliedQuery.sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
                      return (
                        <th key={column.key} aria-sort={ariaSort}>
                          <button
                            type="button"
                            className="btn btn-ghost-secondary btn-sm p-0 border-0 bg-transparent d-inline-flex align-items-center gap-1 fw-semibold text-start"
                            onClick={(event) => onColumnSortClick(column.key, event)}
                            aria-label={`Sort by ${column.label}${isActive ? ` (${appliedQuery.sortDir === 'asc' ? 'ascending' : 'descending'})` : ''}`}
                          >
                            <span>{column.label}</span>
                            <SortIndicatorIcon active={isActive} sortDir={appliedQuery.sortDir} />
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {!loading && !error && displayedMessages.length === 0 ? (
                    <tr>
                      <td className="text-secondary" colSpan={8}>
                        {hasAppliedFilters
                          ? 'No messages matched your current filters.'
                          : 'No messages are available for the current account and date scope.'}
                      </td>
                    </tr>
                  ) : null}
                  {displayedMessages.map((message) => (
                    <tr
                      key={message.prontoMessageId}
                      role="button"
                      tabIndex={0}
                      onClick={() => showSmsDetail(message)}
                      onKeyDown={(event) => event.key === 'Enter' && showSmsDetail(message)}
                      className="cursor-pointer"
                    >
                      <td>{formatPortalDateTime(message.createdAt, timeDisplayMode)}</td>
                      <td>{message.reference ?? '\u2014'}</td>
                      <td>{message.templateId ?? '\u2014'}</td>
                      <td>{message.type ?? 'SMS'}</td>
                      <td>{message.direction}</td>
                      <td>{message.fromNumber}</td>
                      <td>{message.toNumber}</td>
                      <td>
                        <div>{message.status}</div>
                        {message.errorCategory || message.errorReason ? (
                          <div className="small text-secondary">
                            {message.errorCategory ?? '-'}{message.errorReason ? `: ${message.errorReason}` : ''}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
        {activeTab === 'whatsapp' && visibleTabs.includes('whatsapp') ? (
          <div className="card-body text-secondary">WhatsApp messaging support will appear here soon.</div>
        ) : null}
      </div>
      {searchModalOpen ? (
        <div
          className="modal modal-blur fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            if (event.target === event.currentTarget && !searchSubmitting) {
              setSearchModalOpen(false);
            }
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Search Messages</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setSearchModalOpen(false)}
                  disabled={searchSubmitting}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label mb-1">Search by</label>
                  <select
                    className="form-select"
                    value={searchMode}
                    onChange={(event) => setSearchMode(event.target.value as SearchMode)}
                    disabled={searchSubmitting}
                  >
                    <option value="prontoMessageId">Pronto Message ID</option>
                    <option value="reference">Reference</option>
                  </select>
                </div>
                {searchMode === 'prontoMessageId' ? (
                  <div>
                    <label className="form-label mb-1">Pronto Message ID</label>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Paste pronto_message_id"
                      value={searchProntoMessageId}
                      onChange={(event) => setSearchProntoMessageId(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          void submitMessageSearch();
                        }
                      }}
                      disabled={searchSubmitting}
                    />
                  </div>
                ) : (
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label mb-1">Reference</label>
                      <input
                        className="form-control"
                        type="text"
                        placeholder="Enter reference"
                        value={searchReference}
                        onChange={(event) => setSearchReference(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void submitMessageSearch();
                          }
                        }}
                        disabled={searchSubmitting}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label mb-1">Channel scope</label>
                      <select
                        className="form-select"
                        value={searchChannelScope}
                        onChange={(event) => setSearchChannelScope(event.target.value as SearchChannelScope)}
                        disabled={searchSubmitting}
                      >
                        <option value="all">All channels</option>
                        <option value="SMS">SMS</option>
                        <option value="MMS">MMS</option>
                        <option value="RCS">RCS</option>
                      </select>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label mb-1">Direction scope</label>
                      <select
                        className="form-select"
                        value={searchDirectionScope}
                        onChange={(event) => setSearchDirectionScope(event.target.value as DirectionFilter)}
                        disabled={searchSubmitting}
                      >
                        <option value="all">All directions</option>
                        <option value="outbound">Outbound</option>
                        <option value="inbound">Inbound</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-ghost-secondary"
                  onClick={() => setSearchModalOpen(false)}
                  disabled={searchSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    void submitMessageSearch();
                  }}
                  disabled={searchSubmitting}
                >
                  {searchSubmitting ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {(smsDetail !== null || smsDetailLoading) && (
        <div
          className="modal modal-blur fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{smsDetailLoading ? 'Loading...' : 'Message Detail'}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => {
                    setSmsDetail(null);
                    setSelectedMessage(null);
                    setLifecyclePayloadModal(null);
                    setWebhookAttemptsModal(null);
                  }}
                />
              </div>
              <div className="modal-body">
                {smsDetailLoading ? (
                  <div className="text-secondary">Loading...</div>
                ) : smsDetail?.message ? (
                  <div>
                    <div className="mb-3">
                      <div className="text-secondary small fw-semibold text-uppercase">Message ID</div>
                      <div className="font-monospace text-break">{smsDetail.message.prontoMessageId}</div>
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-6">
                        <dl className="row mb-0">
                          <dt className="col-sm-3">Customer Reference</dt>
                          <dd className="col-sm-9">{smsDetail.message.reference ?? '\u2014'}</dd>
                          <dt className="col-sm-3">Template ID</dt>
                          <dd className="col-sm-9">{smsDetail.message.templateId ?? '\u2014'}</dd>
                          <dt className="col-sm-3">From</dt>
                          <dd className="col-sm-9">{smsDetail.message.fromNumber}</dd>
                          <dt className="col-sm-3">To</dt>
                          <dd className="col-sm-9">{smsDetail.message.toNumber}</dd>
                          <dt className="col-sm-3">Status</dt>
                          <dd className="col-sm-9">{smsDetail.message.status}</dd>
                          <dt className="col-sm-3">Category</dt>
                          <dd className="col-sm-9">{smsDetail.message.errorCategory ?? '\u2014'}</dd>
                          <dt className="col-sm-3">Reason</dt>
                          <dd className="col-sm-9">{smsDetail.message.errorReason ?? '\u2014'}</dd>
                          <dt className="col-sm-3">Segments</dt>
                          <dd className="col-sm-9">{smsDetail.message.segments ?? '-'}</dd>
                          <dt className="col-sm-3">Channel</dt>
                          <dd className="col-sm-9">{smsDetail.message.type}</dd>
                          <dt className="col-sm-3">Created</dt>
                          <dd className="col-sm-9">{formatPortalDateTime(smsDetail.message.createdAt, timeDisplayMode)}</dd>
                        </dl>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <h6 className="mb-0">Message Body</h6>
                        </div>
                        <div className="message-body-preview-panel">
                          {messagePreviewParts.length === 0 ? (
                            <div className="text-secondary small">No message body</div>
                          ) : (
                            <div className="message-body-preview-parts">
                              {messagePreviewParts.map((part) => (
                                <MessagePreviewPartRenderer key={part.id} part={part} />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <h6>Lifecycle Events</h6>
                      {(() => {
                        const stepModels = lifecycleSteps.map((step) => {
                          const lifecycleStep = getLifecycleStepState(lifecycleDetail, step.id);
                          const hasEvent =
                            lifecycleStep !== null &&
                            ((typeof lifecycleStep.timestamp === 'string' && lifecycleStep.timestamp.length > 0) ||
                              lifecycleStep.status !== 'PENDING');
                          const isSuccess = isLifecycleSuccess(lifecycleStep?.status ?? null);
                          const isError = hasEvent && !isSuccess;
                          const isPending = !hasEvent;
                          return {
                            step,
                            lifecycleStep,
                            isSuccess,
                            isError,
                            isPending
                          };
                        });
                        const firstPendingIndex = stepModels.findIndex((model) => model.isPending);
                        const activeIndex = firstPendingIndex >= 0 ? firstPendingIndex : stepModels.length - 1;

                        return (
                          <div className="lifecycle-steps">
                            {stepModels.map((model, index) => {
                              const dotClass = model.isSuccess
                                ? 'is-success'
                                : model.isError
                                  ? 'is-error'
                                  : 'is-pending';
                              const nextModel =
                                index < stepModels.length - 1 ? stepModels[index + 1] : null;
                              const lineClass = nextModel
                                ? nextModel.isSuccess
                                  ? 'is-success'
                                  : nextModel.isError
                                    ? 'is-error'
                                    : 'is-pending'
                                : '';
                              const labelClass = index === activeIndex ? 'is-active' : model.isPending ? 'is-pending' : '';
                              const eventKey = `${model.step.id}-${model.lifecycleStep?.timestamp ?? 'pending'}-${
                                model.lifecycleStep?.status ?? 'none'
                              }`;

                              const isFirstStep = index === 0;
                              const isWebhookStep = model.step.id === 'DLRSent' || model.step.id === 'Sent';
                              const canOpenPayload =
                                Boolean(model.lifecycleStep) &&
                                (isFirstStep || isWebhookStep || Boolean(model.lifecycleStep?.webhookRef));

                              return (
                                <div
                                  key={eventKey}
                                  className={`lifecycle-step${canOpenPayload ? ' lifecycle-step--webhook-hover-host' : ''}`}
                                  role={canOpenPayload ? 'button' : undefined}
                                  tabIndex={canOpenPayload ? 0 : undefined}
                                  aria-label={canOpenPayload ? `Open ${model.step.label} payload` : undefined}
                                  onClick={() => {
                                    if (canOpenPayload && model.lifecycleStep) {
                                      openLifecyclePayloadModal(model.lifecycleStep, selectedDirection);
                                    }
                                  }}
                                  onKeyDown={(event) => {
                                    if (
                                      canOpenPayload &&
                                      model.lifecycleStep &&
                                      (event.key === 'Enter' || event.key === ' ')
                                    ) {
                                      event.preventDefault();
                                      openLifecyclePayloadModal(model.lifecycleStep, selectedDirection);
                                    }
                                  }}
                                >
                                  <div className="lifecycle-step-top">
                                    <div
                                      className={`lifecycle-step-dot ${dotClass}`}
                                      aria-label={
                                        model.isSuccess
                                          ? 'Success lifecycle status'
                                          : model.isError
                                            ? 'Failed lifecycle status'
                                            : 'Pending lifecycle status'
                                      }
                                    >
                                      {index + 1}
                                    </div>
                                    {nextModel ? <div className={`lifecycle-step-line ${lineClass}`} /> : null}
                                  </div>
                                  <div className={`lifecycle-step-label small text-center ${labelClass}`}>
                                    {model.step.label}
                                  </div>
                                  <div className="text-secondary small text-center">
                                    {(() => {
                                      const timestampParts = formatPortalDateParts(
                                        model.lifecycleStep?.timestamp ?? null,
                                        timeDisplayMode
                                      );
                                      if (!timestampParts) {
                                        return '\u2014';
                                      }
                                      if (timestampParts.time.length === 0) {
                                        return timestampParts.date;
                                      }
                                      return (
                                        <>
                                          <div>{timestampParts.date}</div>
                                          <div>{timestampParts.time}</div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="text-secondary">Message not found</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {lifecyclePayloadModal ? (
        <div
          className="modal modal-blur fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setLifecyclePayloadModal(null);
            }
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{lifecyclePayloadModal.title}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setLifecyclePayloadModal(null)}
                />
              </div>
              <div className="modal-body">
                <div className="text-secondary small mb-2">{lifecyclePayloadModal.subtitle}</div>
                <pre className="message-webhook-payload-modal-pre mb-0">{lifecyclePayloadModal.payloadText}</pre>
                {lifecyclePayloadModal.webhookRef ? (
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        void openWebhookAttemptModal(
                          lifecyclePayloadModal.webhookRef as NonNullable<MessageLifecycleStep['webhookRef']>,
                          selectedDirection
                        );
                      }}
                    >
                      View webhook attempts
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {webhookAttemptsModal ? (
        <div
          className="modal modal-blur fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setWebhookAttemptsModal(null);
            }
          }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{webhookAttemptsModal.title}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setWebhookAttemptsModal(null)}
                />
              </div>
              <div className="modal-body">
                <div className="text-secondary small mb-2">{webhookAttemptsModal.subtitle}</div>
                {webhookAttemptsModal.loading ? (
                  <div className="text-secondary small">Loading webhook attempts...</div>
                ) : webhookAttemptsModal.error ? (
                  <div className="alert alert-danger py-2 mb-0">{webhookAttemptsModal.error}</div>
                ) : webhookAttemptsModal.details ? (
                  <>
                    <div className="mb-2">
                      <span className="text-secondary">Job</span>{' '}
                      <code>{webhookAttemptsModal.details.job?.id ?? '\u2014'}</code>{' '}
                      <span className="text-secondary ms-2">Status</span>{' '}
                      <code>{webhookAttemptsModal.details.job?.status ?? '\u2014'}</code>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm table-vcenter">
                        <thead>
                          <tr>
                            <th>Attempt</th>
                            <th>Time</th>
                            <th>Will Retry</th>
                            <th>Error / Response</th>
                          </tr>
                        </thead>
                        <tbody>
                          {webhookAttemptsModal.details.attempts.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-secondary">No attempts recorded.</td>
                            </tr>
                          ) : (
                            webhookAttemptsModal.details.attempts.map((attempt) => (
                              <tr key={`${attempt.jobId}-${attempt.id}`}>
                                <td>{attempt.attemptNumber}</td>
                                <td>{formatPortalDateTime(attempt.attemptedAt, timeDisplayMode)}</td>
                                <td>{attempt.willRetry ? 'Yes' : 'No'}</td>
                                <td className="small">{attempt.errorMessage ?? attempt.responseBody ?? '\u2014'}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-secondary small">No webhook attempt data available.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
